import { useEffect, useState } from 'react'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import { rem } from 'polished'
import styled from 'styled-components'
import Modal from 'LuxKit/Modal/Modal'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import { getRemainingFlightSessionTime } from './utils'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { useIntl, FormattedMessage } from 'react-intl'

const FlightSessionCountdownClockContainer = styled.div<{ $summary?: boolean }>`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  grid-gap: ${props => rem(props.$summary ? 8 : 12)};
  justify-content: center;
`

const DescriptionAndInfoIcon = styled.div`
  align-items: center;
  display: flex;
`

const ClickableInfoIcon = styled(Clickable)`
  display: flex;
  margin-inline-start: ${rem(4)};
`

const TimeFixedWidth = styled.div`
  display: inline-block;
  min-width: 2ch;
  text-align: center;
`

function convertNumberToTwoIntegerDigits(number: number) {
  return String(number).padStart(2, '0')
}

interface Props {
  layout?: 'banner' | 'summary'
}

export function FlightSessionCountdownClock({ layout = 'banner' }: Props) {
  const [timeRemaining, setTimeRemaining] = useState(getRemainingFlightSessionTime())
  const [infoModalOpen, setInfoModalOpen] = useState(false)
  const intl = useIntl()

  useEffect(() => {
    // This will update the session storage every second
    const interval = setInterval(() => {
      setTimeRemaining(getRemainingFlightSessionTime())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  if (timeRemaining.minutes > FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES) {
    // Normally this will never be true because the countdown starts at FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES.
    // But for charter flights, the only expiry is the minimum lead time for flight bookings, which will normally be
    // much later than the usual 20 minute countdown. So this hides the timer for that case.
    return null
  }

  const priceChangeWarning = isAgentHubEnabled
    ? intl.formatMessage({
        id: '8bwU-7ymguB31bp9Zs0s',
        defaultMessage: 'All flight prices below are the net fare. Flight prices subject to change.',
        description: 'Agent Hub label above flight results explaining listed prices are net fares and can still change during the session',
      })
    : intl.formatMessage({
        id: 'rn_WUcdSfO9bI2hx9TTd',
        defaultMessage: 'Flight prices subject to change',
        description: 'Label warning that flight prices may change in the flights flow',
      })

  const isSummary = layout === 'summary'

  return (
    <>
      <FlightSessionCountdownClockContainer $summary={isSummary}>
        <DescriptionAndInfoIcon>
          <BodyText variant={isSummary ? 'medium' : 'small'} weight={isSummary ? 'normal' : 'bold'}>{priceChangeWarning}</BodyText>
          {!isSummary && <ClickableInfoIcon onClick={() => setInfoModalOpen(true)}>
            <LineInfoCircleIcon size="S" />
          </ClickableInfoIcon>}
        </DescriptionAndInfoIcon>
        <MessagingLabel variant="urgency">
          <FormattedMessage
            id="dSs9KoomCYGp08N-ffL8"
            defaultMessage="{minutes} : {seconds} remaining"
            description="Flight session countdown timer showing minutes and seconds remaining"
            values={{
              minutes: <TimeFixedWidth>{convertNumberToTwoIntegerDigits(timeRemaining.minutes)}</TimeFixedWidth>,
              seconds: <TimeFixedWidth>{convertNumberToTwoIntegerDigits(timeRemaining.seconds)}</TimeFixedWidth>,
            }}
          />
        </MessagingLabel>
      </FlightSessionCountdownClockContainer>
      <Modal
        title={intl.formatMessage({
          id: 'DKV5The5XBuJ7XEfZmrO',
          defaultMessage: 'Prices subject to change',
          description: 'Modal title about flight price changes in the flight search or booking flow',
        })}
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
      >
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'SH0crJuVaXqam6VbPoCb',
            defaultMessage: 'Due to demand and availability, flight prices can fluctuate quickly. We can hold your flights for a short period before your session expires. Secure your flights before prices increase!',
            description: 'Modal body explaining flight price volatility and session expiry',
          })}
        </BodyText>
      </Modal>
    </>
  )
}
